import { buildExperiencesPassportFromOffers } from 'api/mappers/experienceMap'
import { fetchPassportExperienceTicketsById } from 'actions/ExperienceActions'
import useExperienceList from 'hooks/Experiences/useExperienceList'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useOfferTags from 'hooks/Offers/useOfferTags'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { unique } from 'lib/array/arrayUtils'
import { isEligibleExperiencesPassportTicket } from 'lib/checkout/experiences/cart'
import { useContext, useEffect, useMemo } from 'react'
import type { ExperiencesPassport, ExperiencesPassportCheckoutSource, ExperiencesPassportOfferSource } from './experiencesPassportTypes'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'

interface Result {
  passport?: ExperiencesPassport
  checkoutSources: Array<ExperiencesPassportCheckoutSource>
  fetching: boolean
}

interface Options {
  offer?: ExperiencesPassportOfferSource
  filters?: {
    checkIn?: string
    checkOut?: string
  }
  shouldFetch?: boolean
}

const PASSPORT_TAG_OPTIONS = {
  tagType: 'passport' as const,
  includeAssignments: true,
  includedAssignmentEntityTypeIn: ['EXPERIENCE' as const],
}

function isBedbankOffer(offer: ExperiencesPassportOfferSource): offer is App.BedbankOffer {
  return offer.type === OFFER_TYPE_BED_BANK
}

function useExperiencesPassportForOffer({ offer, filters, shouldFetch = true }: Options = {}): Result {
  const { offer: offerPageOffer, bedbankOffer, checkInDate, checkOutDate } = useContext(OfferPageStateContext)
  // An SPP rate can belong to a different LPC offer than the offer that opened
  // the page. An explicit source must therefore win over the page context.
  const resolvedOffer = offer ?? offerPageOffer ?? bedbankOffer
  const checkIn = filters?.checkIn ?? checkInDate
  const checkOut = filters?.checkOut ?? checkOutDate
  const shouldResolveEligibility = shouldFetch
  const dispatch = useAppDispatch()
  const currentCurrency = useAppSelector(state => state.geo.currentCurrency)
  const passportTickets = useAppSelector(state => state.experience.passportTickets)
  const fetchingPassportTickets = useAppSelector(state => state.experience.fetchingPassportTickets)
  const passportTicketErrors = useAppSelector(state => state.experience.passportTicketErrors)
  const { tags: passportTags, fetching: fetchingTags } = useOfferTags(
    shouldResolveEligibility && resolvedOffer ? resolvedOffer.id : undefined,
    PASSPORT_TAG_OPTIONS,
  )

  const passportExperienceIds = useMemo(() => {
    return unique(passportTags.flatMap(tag =>
      tag.assignments
        ?.filter(assignment => assignment.entityType === 'EXPERIENCE')
        .map(assignment => assignment.entityId) ?? [],
    ))
  }, [passportTags])
  const shouldFetchExperiences = shouldResolveEligibility && !fetchingTags && !!passportExperienceIds.length

  const [experienceList, experiences] = useExperienceList({
    experienceIds: shouldFetchExperiences ? passportExperienceIds : undefined,
    showUnlisted: true,
    limit: passportExperienceIds.length || 1,
    from: checkIn,
    to: checkOut,
  })
  const passportExperiences = useMemo(
    () => shouldResolveEligibility ? experiences.filter(experience => experience.isExperiencePassport) : [],
    [experiences, shouldResolveEligibility],
  )

  useEffect(() => {
    if (shouldFetchExperiences) {
      dispatch(fetchPassportExperienceTicketsById(...passportExperienceIds))
    }
  }, [dispatch, passportExperienceIds, shouldFetchExperiences])

  const eligiblePassport = useMemo(() => {
    if (!resolvedOffer || !shouldFetchExperiences || !passportExperiences.length) return undefined
    const ticketsReady = passportExperiences.every(experience =>
      passportTickets[experience.id]?.some(isEligibleExperiencesPassportTicket),
    )
    if (!ticketsReady) return undefined

    const offerWithAddress = {
      ...resolvedOffer,
      ...(isBedbankOffer(resolvedOffer) && { locationHeading: resolvedOffer.property?.address?.city }),
    }

    return buildExperiencesPassportFromOffers(offerWithAddress, passportExperiences, currentCurrency)
  }, [currentCurrency, resolvedOffer, passportExperiences, passportTickets, shouldFetchExperiences])

  const checkoutSources = useMemo(() => {
    const sources = passportExperiences.flatMap(experience => {
      const tickets = passportTickets[experience.id]
      return tickets?.some(isEligibleExperiencesPassportTicket) ? [{ experience, tickets }] : []
    })

    return sources.length === passportExperiences.length ? sources : []
  }, [passportExperiences, passportTickets])

  const fetchingTickets = shouldResolveEligibility && passportExperienceIds.some(id =>
    fetchingPassportTickets[id] || (!(id in passportTickets) && !passportTicketErrors[id]),
  )
  const fetchingEligibility = !!passportExperienceIds.length && (
    fetchingTags || fetchingTickets || (shouldFetchExperiences && !!experienceList.fetching)
  )

  return {
    passport: eligiblePassport,
    checkoutSources,
    fetching: fetchingEligibility,
  }
}

export default useExperiencesPassportForOffer
